<script lang="ts" setup>
import { StateInput } from '@/components/common/inputs/state-input';
import { formatNumber, formatNumberWithDecimal } from 'cesg-shared';
import { useI18n } from 'vue-i18n';
import type { SaveLiftPlanData } from './logic/useLiftPlanLogic';
import {
  useLiftPlanLogic,
  type UseLiftPlanLogicProps,
} from './logic/useLiftPlanLogic';
import { LiftTable } from './sub-components/lift-table';
import { SectionCard } from './sub-components/section-card';

export type LiftPlanProps = UseLiftPlanLogicProps;

const props = defineProps<LiftPlanProps>();
const emit = defineEmits<{ save: [body: SaveLiftPlanData] }>();
const { state, action } = useLiftPlanLogic(props, emit);
const { t } = useI18n();
</script>

<template>
  <SectionCard
    :name="state.liftPlan.name"
    :is-editing="state.isEditing"
    :loading="state.componentState.sectionCard.isSaving"
    :component-state="state.componentState.sectionCard"
    @action="
      (evt) => {
        if (evt.type === 'toggle-edit') action.toggleEdit();
        else if (evt.type === 'cancel') action.cancel();
        else if (evt.type === 'delete') action.delete();
        else if (evt.type === 'update-name')
          action.updateName({ value: evt.value });
      }
    "
  >
    <!-- Summary bar -->
    <div class="summary-bar">
      <div class="eq-table">
        <div class="eq-row">
          <span class="eq-label">{{
            t('projects.liftPlan.summaryBar.contractSum')
          }}</span>
          <span class="eq-value">
            <StateInput
              :mode="state.componentState.contractSum"
              class="inline-input summary-amount-input"
              readonly-class="summary-amount-input"
              inputmode="numeric"
              :model-value="formatNumber(state.liftPlan.contractSum)"
            />
            kr
          </span>
        </div>
        <div
          class="eq-row"
          v-if="state.componentState.entreprenadsumma !== 'hidden'"
        >
          <span class="eq-label">{{
            t('projects.liftPlan.summaryBar.entreprenadsumma')
          }}</span>
          <span class="eq-value">
            <StateInput
              :mode="state.componentState.entreprenadsumma"
              class="inline-input summary-amount-input"
              readonly-class="summary-amount-input"
              inputmode="numeric"
              :model-value="formatNumber(state.liftPlan.entreprenadsumma)"
            />
            kr
          </span>
        </div>
        <div class="eq-row eq-op-row">
          <span class="eq-label">{{
            t('projects.liftPlan.summaryBar.retention')
          }}</span>
          <span class="eq-value">
            <StateInput
              :mode="state.componentState.retentionPercentage"
              class="inline-input pct-input"
              readonly-class="pct-input"
              inputmode="decimal"
              :model-value="
                formatNumberWithDecimal(state.liftPlan.retentionPercentage, 1)
              "
              @update:model-value="
                action.updateRetentionPercentage({ value: $event })
              "
            />
            %
          </span>
        </div>
        <div class="eq-divider"></div>
        <div class="eq-row eq-result-row">
          <span class="eq-label">{{
            t('projects.liftPlan.summaryBar.retentionTotal')
          }}</span>
          <span class="eq-value eq-result">
            <StateInput
              :mode="state.componentState.retentionAmount"
              class="inline-input summary-amount-input"
              readonly-class="summary-amount-input"
              inputmode="numeric"
              :model-value="
                formatNumber(
                  Math.round(
                    state.liftPlan.contractSum *
                      (state.liftPlan.retentionPercentage / 100),
                  ),
                )
              "
            />
            kr
          </span>
        </div>
      </div>
    </div>

    <LiftTable
      :columns="state.columns"
      :subtotal-columns="state.subtotalColumns"
      :component-state="state.componentState.liftTable"
      :entries="state.liftPlan.entries"
      :subtotal-rows="state.liftPlan.subtotalRows"
      :total-amount="state.liftPlan.totalAmount"
      :total-retention="state.liftPlan.totalRetention"
      :total-amount-due="state.liftPlan.totalAmountDue"
      @action="
        (evt) => {
          if (evt.type === 'add-entry') action.addEntry();
          else if (evt.type === 'delete-entry')
            action.deleteEntry({ id: evt.id });
          else if (evt.type === 'update-entry')
            action.updateEntry({
              id: evt.id,
              field: evt.field,
              value: evt.value,
            });
          else if (evt.type === 'add-subtotal') action.addSubtotal();
          else if (evt.type === 'delete-subtotal')
            action.deleteSubtotal({ id: evt.id });
          else if (evt.type === 'update-subtotal')
            action.updateSubtotal({
              id: evt.id,
              field: evt.field,
              value: evt.value,
            });
        }
      "
    />
  </SectionCard>
</template>

<style scoped>
.inline-input {
  background: transparent;
  border: none;
  box-shadow: inset 0 -1px 0 #aaa;
  outline: none;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: inherit;
  transition: box-shadow 0.15s;
}

.inline-input:hover {
  box-shadow: inset 0 -1px 0 #666;
}

.inline-input:focus {
  box-shadow: inset 0 -1px 0 var(--primary-color);
}

.pct-input {
  display: inline-block;
  width: 4rem;
  text-align: right;
}

.summary-amount-input {
  display: inline-block;
  width: 8rem;
  text-align: right;
}

:deep(.section-card-content) {
  background-color: #fafafc;
}

.summary-bar {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fafafc;
}

.eq-table {
  display: inline-grid;
  grid-template-columns: auto auto;
  gap: 0;
}

.eq-row {
  display: contents;
}

.eq-row > .eq-label,
.eq-row > .eq-value {
  padding: 0.35rem 0.75rem 0.35rem 0;
  font-size: 0.9rem;
  color: #444;
  white-space: nowrap;
}

.eq-row > .eq-value {
  text-align: right;
  font-weight: 600;
  color: #222;
  padding-right: 0;
  padding-left: 2.5rem;
}

.eq-label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: #555;
}

.eq-divider {
  grid-column: 1 / -1;
  border-top: 1px solid #d0d0d6;
  margin: 0.3rem 0;
}

.eq-result {
  color: var(--primary-color) !important;
  font-weight: 700;
  font-size: 1rem;
}

.eq-result-row > .eq-label {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

:deep(.section-card-content) .table {
  background-color: #fff;
}
</style>
